import {ContactRelationship, User} from "models"
import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useI18n, View} from "shared/base"
import usersUpdateSession, {select} from "components/users/update-session"
import AppHistory from "shared/history"
import Button from "./button"
import ButtonUnderscore from "./button-underscore"
import Card from "./card"
import Checkbox from "./checkbox"
import Devise from "@kaspernj/api-maker/build/devise"
import {digg} from "diggerize"
import emailValidator from "email-validator"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import Input from "./input"
import {isCurrentUserA} from "components/users/is-a"
import React from "react"
import Routes from "shared/routes"

export default memo(shapeComponent(class ComponentsChalkyLayoutSignIn extends ShapeComponent {
  static defaultProps = {
    redirectAfterSignIn: false,
    onSignIn: null
  }

  static propTypes = propTypesExact({
    redirectAfterSignIn: PropTypes.bool.isRequired,
    onSignIn: PropTypes.func
  })

  setup() {
    const {t} = useI18n({namespace: "js.components.chalky_layout.sign_in"})

    this.t = t
    this.useStates({
      form: null,
      validEmail: false,
      validPassword: false
    })
  }

  render() {
    const {t} = this.tt

    return (
      <Card style={this.cardStyle ||= {maxWidth: 640, width: "100%"}} testID="chalky-layout/sign-in">
        <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
          <View testID="sign-in-form-container">
            <Text
              style={this.welcomeBackStyle ||= {
                color: "#2c2c96",
                fontFamily: "DreamChaser",
                fontSize: 40,
                textAlign: "center"
              }}
              testID="sign-in-welcome-back-text"
            >
              {t(".welcome_back")}
            </Text>
            <Text
              style={this.enterDetailsStyle ||= {
                color: "#2c2c96",
                fontSize: 16,
                fontWeight: 500,
                marginBottom: 16,
                textAlign: "center"
              }}
              testID="sign-in-enter-details-text"
            >
              {t(".please_enter_your_details_below")}
            </Text>
            <Input
              label={t(".email")}
              name="email"
              onChangeText={this.tt.onEmailChange}
              style={this.emailInputStyle ||= {marginBottom: 16}}
              testID="email-input"
            />
            <Input
              label={t(".password")}
              name="password"
              onChangeText={this.tt.onPasswordChange}
              secureTextEntry
              style={this.passwordInputStyle ||= {marginBottom: 16}}
              testID="password-input"
            />
            <View
              style={this.rememberForgotContainerStyle ||= {alignItems: "center", flexDirection: "row", justifyContent: "space-between"}}
              testID="sign-in-remember-forgot-container"
            >
              <Checkbox defaultChecked={false} label={t(".remember_me")} name="remember_me" testID="remember-me-checkbox" />
              <ButtonUnderscore
                testID="missing-confirmation-button"
                title={t(".missing_confirmation_question_mark")}
                to={Routes.frontUsersResendConfirmationEmailPath()}
              />
              <ButtonUnderscore
                testID="forgot-password-button"
                title={t(".forgot_password_question_mark")}
                to={Routes.frontUsersForgotPasswordPath()}
              />
            </View>
            <Button
              disabled={!this.s.validEmail || !this.s.validPassword}
              onPress={this.tt.onSignInButtonPress}
              primary
              style={this.signInButtonStyle ||= {marginTop: 16}}
              testID="sign-in-button"
              title={t(".sign_in")}
            />
            <View
              style={this.orContainerStyle ||= {alignItems: "center", marginVertical: 20}}
              testID="sign-in-or-container"
            >
              <Text testID="sign-in-or-text">
                {t(".or")}
              </Text>
            </View>
            <Button
              testID="create-account-button"
              title={t(".create_account")}
              to={Routes.userCreateUserStep1Path()}
            />
          </View>
        </Form>
      </Card>
    )
  }

  onEmailChange = (email) => this.setState({validEmail: emailValidator.validate(email)})
  onPasswordChange = (password) => this.setState({validPassword: Boolean(password)})
  onSignInButtonPress = async () => await this.s.form.submit()

  onSubmit = async () => {
    const {t} = this.tt
    const {onSignIn, redirectAfterSignIn} = this.p
    const formData = this.s.form.asObject()
    const email = digg(formData, "email")
    const password = digg(formData, "password")
    const rememberMe = digg(formData, "remember_me")

    try {
      await Devise.signIn(
        email,
        password,
        {
          loadQuery: User
            .ransack()
            .preload(["contact.parent_relationships.contact_relationship_type", "user_roles.role"])
            .select(select),
          rememberMe
        }
      )
      await usersUpdateSession(Devise.currentUser().id())

      FlashMessage.success(t(".you_were_signed_in"))

      if (onSignIn) {
        onSignIn()
      }

      if (redirectAfterSignIn) {
        this.redirect()
      }
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  redirect = async () => {
    const schoolLeaderRole = await ContactRelationship
      .ransack({
        contact_relationship_type_identifier_eq: "school_leader",
        child_id_eq: Devise.currentUser().contactId()
      })
      .first()

    if (schoolLeaderRole) {
      AppHistory.push(Routes.userClassStatusPath())
    } else if (isCurrentUserA("admin", "sales_rep", "hacker")) {
      AppHistory.push(Routes.adminRootPath())
    } else if (isCurrentUserA(["student", "teacher"])) {
      AppHistory.push(Routes.userRootPath())
    }
  }
}))
